import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LOGO_URL, CONTACT, IMG, APPLICATION_IMAGES } from "@/lib/brand";
import Reveal from "@/components/site/Reveal";
import BeforeAfter from "@/components/site/BeforeAfter";
import { Instagram, Phone, MapPin, ArrowUpRight, Check, Award, Shield, Droplets, Ruler, Layers, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- HERO ---------- */
const Hero = () => (
  <section id="top" className="relative min-h-[92vh] flex items-end overflow-hidden">
    <div className="absolute inset-0">
      <img
        src="https://images.unsplash.com/photo-1714924674541-9e11b2d1dd1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtaW5pbWFsJTIwY29uY3JldGUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODMwODE1MDJ8MA&ixlib=rb-4.1.0&q=85"
        alt="GA6 microcement interior"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/10 to-black/40" />
    </div>
    <div className="relative ga-container pb-16 md:pb-24 text-white w-full">
      <Reveal className="max-w-4xl">
        <div className="overline text-white/80 mb-6" data-testid="hero-overline">The Seamless Surface · Made in India</div>
        <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight">
          Concrete,<br />
          <em className="italic font-light">reimagined</em> as a surface.
        </h1>
        <p className="mt-8 max-w-2xl text-base md:text-lg text-white/85 font-light leading-relaxed">
          GA6 is a premium microcement — a 2–3&nbsp;mm coating applied over any surface to deliver
          seamless, joint-free walls, floors, kitchens & bathrooms across India.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <button
            data-testid="hero-cta-primary"
            className="ga-btn"
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
          >
            Request Sample Kit <span className="arrow">→</span>
          </button>
          <button
            data-testid="hero-cta-secondary"
            className="ga-btn ga-btn-ghost"
            style={{ background: "transparent", color: "#fff", borderColor: "rgba(255,255,255,0.6)" }}
            onClick={() => document.getElementById("finishes")?.scrollIntoView({ behavior: "smooth" })}
          >
            Explore Finishes
          </button>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------- MARQUEE ---------- */
const Marquee = () => {
  const items = ["Seamless", "Waterproof", "2–3mm Thin", "Pan-India Applicators", "Interior + Exterior", "Custom Palettes"];
  const doubled = [...items, ...items, ...items];
  return (
    <section aria-label="Highlights" className="py-6 md:py-8 border-y border-[color:var(--ga-border)] overflow-hidden bg-[color:var(--ga-bg-2)]">
      <div className="flex gap-16 marquee-track whitespace-nowrap">
        {doubled.map((t, i) => (
          <div key={i} className="flex items-center gap-16 overline text-[color:var(--ga-ink)]">
            <span>{t}</span>
            <span>◇</span>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- ABOUT ---------- */
const About = () => (
  <section id="about" className="py-24 md:py-32">
    <div className="ga-container grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
      <Reveal className="lg:col-span-5">
        <div className="overline mb-6">About GA6</div>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-light tracking-tight">
          A studio-grade microcement, crafted for Indian architecture.
        </h2>
      </Reveal>
      <Reveal className="lg:col-span-6 lg:col-start-7" delay={120}>
        <p className="text-base md:text-lg text-[color:var(--ga-ink-2)] leading-relaxed font-light">
          GA6 is a next-generation microcement engineered for humid coastal weather, monsoon exposure
          and the demands of modern Indian homes and commercial spaces. Applied in a single
          continuous surface — with no joints, no grout, no tiles — GA6 transforms floors, walls,
          bathrooms and kitchens into architectural statements.
        </p>
        <div className="grid grid-cols-2 gap-8 mt-12">
          {[
            { k: "20+", v: "Signature colours" },
            { k: "50+", v: "Certified applicators" },
            { k: "16", v: "Indian cities served" },
            { k: "10yr", v: "Surface longevity" },
          ].map((s) => (
            <div key={s.v} className="border-t border-[color:var(--ga-border)] pt-6">
              <div className="font-display text-4xl md:text-5xl">{s.k}</div>
              <div className="overline mt-2">{s.v}</div>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
    {/* Feature list */}
    <div className="ga-container grid grid-cols-1 md:grid-cols-3 gap-8 mt-24">
      {[
        { icon: Layers, t: "Just 2–3 mm thick", d: "Applied over any existing surface — tiles, drywall, plywood, concrete." },
        { icon: Droplets, t: "100% waterproof", d: "Fully sealed with a UV-resistant top coat for showers, kitchens and exteriors." },
        { icon: Shield, t: "Impact & scratch resistant", d: "Certified surface hardness suitable for high-traffic residential and commercial use." },
        { icon: Sparkles, t: "Seamless & joint-free", d: "No grout lines, no tile repetition — one continuous architectural surface." },
        { icon: Ruler, t: "Custom colour matching", d: "Bespoke palettes on request. Match Pantone, RAL, or your interior scheme." },
        { icon: Award, t: "Trained applicators only", d: "Applied exclusively by GA6-certified installers across India." },
      ].map(({ icon: Icon, t, d }, i) => (
        <Reveal key={t} delay={i * 60} className="p-8 bg-white border border-[color:var(--ga-border)]">
          <Icon size={22} strokeWidth={1.25} />
          <div className="mt-6 font-display text-2xl">{t}</div>
          <p className="mt-3 text-sm text-[color:var(--ga-ink-2)] leading-relaxed">{d}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------- FINISHES ---------- */
const Finishes = ({ finishes, textures }) => {
  const families = ["All", "Neutrals", "Earth", "Darks"];
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? finishes : finishes.filter((f) => f.family === active);

  return (
    <section id="finishes" className="py-24 md:py-32 bg-[color:var(--ga-bg-2)] relative grain">
      <div className="ga-container relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <Reveal>
            <div className="overline mb-4">Finishes & Colours</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] max-w-2xl">
              Twelve signature tones. Endless bespoke possibilities.
            </h2>
          </Reveal>
          <div className="flex gap-2 flex-wrap" data-testid="finishes-filter">
            {families.map((f) => (
              <button
                key={f}
                data-testid={`finish-filter-${f.toLowerCase()}`}
                onClick={() => setActive(f)}
                className={`px-4 py-2 text-xs tracking-[0.2em] uppercase border transition-all ${
                  active === f
                    ? "bg-[color:var(--ga-ink)] text-[color:var(--ga-bg)] border-[color:var(--ga-ink)]"
                    : "bg-transparent text-[color:var(--ga-ink)] border-[color:var(--ga-border)] hover:border-[color:var(--ga-ink)]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8 mt-14">
          {filtered.map((c, i) => (
            <Reveal key={c.id} delay={i * 40} testid={`finish-${c.id}`}>
              <div className="group">
                <div
                  className="aspect-[4/5] w-full relative overflow-hidden"
                  style={{ background: c.hex }}
                >
                  <div className="absolute inset-0 opacity-[0.18] mix-blend-multiply"
                    style={{ backgroundImage: `url(${IMG.texture1})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                </div>
                <div className="mt-4 flex items-start justify-between">
                  <div>
                    <div className="font-display text-2xl leading-none">{c.name}</div>
                    <div className="overline mt-2">{c.family}</div>
                  </div>
                  <span className="text-xs tracking-widest text-[color:var(--ga-muted)]">{c.hex}</span>
                </div>
                <p className="mt-3 text-sm text-[color:var(--ga-ink-2)] leading-relaxed">{c.description}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Textures */}
        <div className="mt-24">
          <div className="overline mb-6">Textures</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {textures.map((t, i) => (
              <Reveal key={t.id} delay={i * 60} className="p-6 bg-white border border-[color:var(--ga-border)]">
                <div className="font-display text-2xl">{t.name}</div>
                <p className="mt-3 text-sm text-[color:var(--ga-ink-2)] leading-relaxed">{t.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- APPLICATIONS ---------- */
const Applications = ({ applications }) => {
  const [active, setActive] = useState(applications[0]?.id);
  const current = applications.find((a) => a.id === active) || applications[0];
  return (
    <section id="applications" className="py-24 md:py-32">
      <div className="ga-container">
        <Reveal>
          <div className="overline mb-4">Applications</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] max-w-3xl">
            One surface. Every space in your home.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-14">
          <div className="lg:col-span-4 flex flex-col divide-y divide-[color:var(--ga-border)] border-t border-b border-[color:var(--ga-border)]">
            {applications.map((a) => (
              <button
                key={a.id}
                data-testid={`application-${a.id}`}
                onClick={() => setActive(a.id)}
                className={`text-left py-6 flex items-center justify-between group transition-colors ${
                  active === a.id ? "text-[color:var(--ga-ink)]" : "text-[color:var(--ga-muted)] hover:text-[color:var(--ga-ink)]"
                }`}
              >
                <span className="font-display text-3xl md:text-4xl">{a.name}</span>
                <ArrowUpRight size={20} strokeWidth={1.25} className={`transition-transform ${active === a.id ? "translate-x-0" : "-translate-x-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"}`} />
              </button>
            ))}
          </div>
          <div className="lg:col-span-8">
            <div className="relative aspect-[4/3] w-full overflow-hidden">
              <img
                key={current.id}
                src={APPLICATION_IMAGES[current.id] || IMG.arch1}
                alt={current.name}
                className="w-full h-full object-cover transition-transform duration-700 ease-out"
              />
            </div>
            <div className="mt-6 max-w-2xl">
              <div className="overline">{current.name}</div>
              <p className="mt-3 text-base md:text-lg text-[color:var(--ga-ink-2)] leading-relaxed font-light">{current.description}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- GALLERY (Before/After + grid) ---------- */
const Gallery = () => (
  <section id="gallery" className="py-24 md:py-32 bg-[color:var(--ga-bg-2)]">
    <div className="ga-container">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <Reveal>
          <div className="overline mb-4">Projects</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] max-w-2xl">
            Real spaces. Before &amp; after.
          </h2>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-14">
        <Reveal className="lg:col-span-7">
          <BeforeAfter
            testid="ba-1"
            before={IMG.bath2}
            after={IMG.bath}
            labelBefore="Before"
            labelAfter="After · GA6 Bone White"
          />
        </Reveal>
        <Reveal className="lg:col-span-5" delay={120}>
          <div className="grid grid-cols-2 gap-4 h-full">
            <img src={IMG.arch1} alt="project" className="w-full h-full object-cover aspect-[3/4]" />
            <img src={IMG.arch2} alt="project" className="w-full h-full object-cover aspect-[3/4] mt-8" />
          </div>
        </Reveal>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 mt-8">
        {[IMG.texture1, IMG.texture2, IMG.intro, IMG.bath, IMG.arch1, IMG.arch2, IMG.training1, IMG.training2].map((src, i) => (
          <Reveal key={i} delay={i * 40}>
            <div className="aspect-square overflow-hidden">
              <img src={src} alt="project" className="w-full h-full object-cover transition-transform duration-700 ease-out hover:scale-105" />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- TRAINING ---------- */
const Training = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", experience: "", program: "Foundation" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.city) {
      toast.error("Please fill in required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/training/apply`, form);
      toast.success("Application received. Our team will contact you within 48 hours.");
      setForm({ name: "", email: "", phone: "", city: "", experience: "", program: "Foundation" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="training" className="py-24 md:py-32">
      <div className="ga-container grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <Reveal className="lg:col-span-6">
          <div className="overline mb-4">Applicator Certification</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
            Become a certified GA6 applicator.
          </h2>
          <p className="mt-6 text-base md:text-lg text-[color:var(--ga-ink-2)] leading-relaxed font-light max-w-xl">
            A structured 5-day intensive covering primer chemistry, layering technique, colour theory,
            waterproofing and finishing. Graduates receive an official GA6 certification and are listed
            in our national dealer network.
          </p>

          <div className="mt-10 space-y-4">
            {[
              "5-day hands-on residential program",
              "Materials, tools & sample kits included",
              "Certification recognised across India",
              "Post-training project support",
            ].map((f) => (
              <div key={f} className="flex items-start gap-3">
                <Check size={18} strokeWidth={1.5} className="mt-1 shrink-0" />
                <span className="text-sm md:text-base">{f}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 grid grid-cols-2 gap-4">
            <img src={IMG.training1} alt="Training" className="w-full aspect-square object-cover" />
            <img src={IMG.training2} alt="Training" className="w-full aspect-square object-cover mt-6" />
          </div>
        </Reveal>

        <Reveal className="lg:col-span-6" delay={120}>
          <form
            onSubmit={submit}
            data-testid="training-form"
            className="p-8 md:p-10 bg-white border border-[color:var(--ga-border)]"
          >
            <div className="overline mb-6">Apply — Training Programme</div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input data-testid="training-name" placeholder="Full name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input data-testid="training-email" type="email" placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input data-testid="training-phone" placeholder="Phone / WhatsApp" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <Input data-testid="training-city" placeholder="City" value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>

            <div className="mt-4">
              <Select value={form.program} onValueChange={(v) => setForm({ ...form, program: v })}>
                <SelectTrigger data-testid="training-program"><SelectValue placeholder="Programme" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Foundation">Foundation — 5 days</SelectItem>
                  <SelectItem value="Advanced">Advanced — 3 days</SelectItem>
                  <SelectItem value="Masterclass">Masterclass — 2 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-4">
              <Textarea data-testid="training-experience" rows={4} placeholder="Briefly describe your experience (optional)"
                value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} />
            </div>

            <button
              type="submit"
              data-testid="training-submit"
              className="ga-btn mt-6 w-full justify-center"
              disabled={loading}
            >
              {loading ? "Submitting…" : "Submit Application"} <span className="arrow">→</span>
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------- DEALERS ---------- */
const Dealers = ({ dealers }) => {
  const [query, setQuery] = useState("");
  const list = dealers.filter((d) =>
    (d.city + " " + d.state).toLowerCase().includes(query.toLowerCase())
  );
  return (
    <section id="dealers" className="py-24 md:py-32 bg-[color:var(--ga-bg-2)]">
      <div className="ga-container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <Reveal>
            <div className="overline mb-4">Available Across India</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] max-w-2xl">
              Find your nearest GA6 dealer.
            </h2>
          </Reveal>
          <div className="w-full md:w-80">
            <Input
              data-testid="dealer-search"
              placeholder="Search city or state…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-2 mt-14">
          {list.map((d, i) => (
            <Reveal key={d.city} delay={i * 30}>
              <div className="flex items-center justify-between py-5 border-b border-[color:var(--ga-border)]">
                <div>
                  <div className="font-display text-2xl">{d.city}</div>
                  <div className="overline mt-1">{d.state} · {d.address}</div>
                </div>
                <a
                  href={`tel:${d.phone.replace(/\s/g, "")}`}
                  data-testid={`dealer-${d.city.toLowerCase().replace(/\s/g, "-")}`}
                  className="ga-link text-sm tracking-widest uppercase"
                >
                  Call
                </a>
              </div>
            </Reveal>
          ))}
          {list.length === 0 && (
            <div className="col-span-full text-center py-16 text-[color:var(--ga-muted)]">
              No dealer found in that city. <button className="ga-link" onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}>Enquire directly →</button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/* ---------- JOURNAL ---------- */
const Journal = ({ posts }) => (
  <section id="journal" className="py-24 md:py-32">
    <div className="ga-container">
      <Reveal>
        <div className="overline mb-4">Journal</div>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] max-w-2xl">
          Notes on surface, material & light.
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-14">
        {posts.map((p, i) => (
          <Reveal key={p.slug} delay={i * 80} testid={`journal-${p.slug}`}>
            <a href={`#${p.slug}`} className="group block" onClick={(e) => e.preventDefault()}>
              <div className="aspect-[4/5] overflow-hidden">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              </div>
              <div className="overline mt-6">{new Date(p.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · {p.read_time}</div>
              <div className="mt-3 font-display text-2xl leading-tight group-hover:opacity-70 transition-opacity">{p.title}</div>
              <p className="mt-3 text-sm text-[color:var(--ga-ink-2)] leading-relaxed">{p.excerpt}</p>
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- FAQ ---------- */
const FAQ = () => (
  <section className="py-24 md:py-32 bg-[color:var(--ga-bg-2)]">
    <div className="ga-container grid grid-cols-1 lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-4">
        <div className="overline mb-4">FAQ</div>
        <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
          Questions,<br />answered.
        </h2>
      </Reveal>
      <div className="lg:col-span-8">
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {[
            { q: "Can GA6 be applied over existing tiles?", a: "Yes. GA6 bonds beautifully over ceramic tiles, marble, concrete, drywall, plywood — with correct priming. No demolition required." },
            { q: "Is it truly waterproof?", a: "Once sealed with our two-part polyurethane top coat, GA6 is fully waterproof and suitable for shower walls, bathroom floors, kitchens and even swimming pool surrounds." },
            { q: "How long does installation take?", a: "A typical 500 sq ft area takes 4–6 days with a two-person team, including primer, base coats, finish coats and sealer." },
            { q: "Where in India do you deliver?", a: "We deliver material and provide certified applicators across all major Indian cities. See our dealer list above." },
            { q: "What is the maintenance like?", a: "Just neutral pH cleaners and a damp cloth. Re-sealing is recommended every 3–5 years in high-traffic areas." },
            { q: "Can I get a custom colour?", a: "Yes. We offer bespoke colour matching to Pantone, RAL or any physical sample you provide." },
          ].map((item, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-b border-[color:var(--ga-border)]">
              <AccordionTrigger className="font-display text-xl md:text-2xl py-6 text-left hover:no-underline">{item.q}</AccordionTrigger>
              <AccordionContent className="text-[color:var(--ga-ink-2)] leading-relaxed text-base pb-6">{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

/* ---------- CONTACT ---------- */
const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", inquiry_type: "General", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you. Our team will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", city: "", inquiry_type: "General", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32">
      <div className="ga-container grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <Reveal className="lg:col-span-5">
          <div className="overline mb-4">Get In Touch</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
            Start your GA6 project.
          </h2>
          <p className="mt-6 text-base md:text-lg text-[color:var(--ga-ink-2)] leading-relaxed font-light max-w-md">
            Send us your project details — residential, hospitality or commercial. We&apos;ll respond
            with a sample kit, budget estimate and applicator availability in your city.
          </p>

          <div className="mt-10 space-y-6">
            <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-4 group" data-testid="contact-phone">
              <Phone size={18} strokeWidth={1.25} />
              <div>
                <div className="overline">Phone / WhatsApp</div>
                <div className="text-lg font-display group-hover:opacity-70 transition-opacity">{CONTACT.phoneDisplay}</div>
              </div>
            </a>
            <a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-4 group" data-testid="contact-instagram">
              <Instagram size={18} strokeWidth={1.25} />
              <div>
                <div className="overline">Instagram</div>
                <div className="text-lg font-display group-hover:opacity-70 transition-opacity">{CONTACT.instagramHandle}</div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <MapPin size={18} strokeWidth={1.25} />
              <div>
                <div className="overline">Showroom & HQ</div>
                <div className="text-lg font-display">India · Pan-India shipping</div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={120}>
          <form
            onSubmit={submit}
            className="p-8 md:p-10 bg-white border border-[color:var(--ga-border)]"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input data-testid="contact-name" placeholder="Full name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input data-testid="contact-email" type="email" placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input data-testid="contact-phone-input" placeholder="Phone / WhatsApp" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <Input data-testid="contact-city" placeholder="City" value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>

            <div className="mt-4">
              <Select value={form.inquiry_type} onValueChange={(v) => setForm({ ...form, inquiry_type: v })}>
                <SelectTrigger data-testid="contact-inquiry-type"><SelectValue placeholder="Inquiry type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="General">General enquiry</SelectItem>
                  <SelectItem value="Residential">Residential project</SelectItem>
                  <SelectItem value="Commercial">Commercial project</SelectItem>
                  <SelectItem value="Sample">Sample kit request</SelectItem>
                  <SelectItem value="Dealer">Become a dealer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-4">
              <Textarea data-testid="contact-message" rows={5} placeholder="Tell us about your project…"
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>

            <button
              type="submit"
              data-testid="contact-submit"
              className="ga-btn mt-6 w-full justify-center"
              disabled={loading}
            >
              {loading ? "Sending…" : "Send Enquiry"} <span className="arrow">→</span>
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------- FOOTER ---------- */
const Footer = () => (
  <footer className="pt-16 pb-10 border-t border-[color:var(--ga-border)] bg-[color:var(--ga-bg)]">
    <div className="ga-container">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <img src={LOGO_URL} alt="GA6" className="h-10 w-auto" />
          <p className="mt-6 max-w-sm text-sm text-[color:var(--ga-ink-2)] leading-relaxed">
            GA6 · Premium microcement for Indian architecture. Seamless surfaces, delivered pan-India.
          </p>
        </div>
        <div className="md:col-span-2">
          <div className="overline mb-4">Explore</div>
          <ul className="space-y-3 text-sm">
            <li><a href="#finishes" className="ga-link">Finishes</a></li>
            <li><a href="#applications" className="ga-link">Applications</a></li>
            <li><a href="#gallery" className="ga-link">Gallery</a></li>
            <li><a href="#journal" className="ga-link">Journal</a></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <div className="overline mb-4">Company</div>
          <ul className="space-y-3 text-sm">
            <li><a href="#about" className="ga-link">About</a></li>
            <li><a href="#training" className="ga-link">Training</a></li>
            <li><a href="#dealers" className="ga-link">Dealers</a></li>
            <li><a href="#contact" className="ga-link">Contact</a></li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <div className="overline mb-4">Contact</div>
          <ul className="space-y-3 text-sm">
            <li><a href={`tel:${CONTACT.phone}`} className="ga-link">{CONTACT.phoneDisplay}</a></li>
            <li><a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="ga-link">{CONTACT.instagramHandle}</a></li>
          </ul>
        </div>
      </div>
      <div className="mt-14 pt-6 border-t border-[color:var(--ga-border)] flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs tracking-widest uppercase text-[color:var(--ga-muted)]">
        <div>© {new Date().getFullYear()} GA6® · Made in India</div>
        <div>Microcement · Seamless surface systems</div>
      </div>
    </div>
  </footer>
);

/* ---------- WA float ---------- */
const WhatsAppFloat = () => (
  <a
    data-testid="whatsapp-float"
    href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Hi GA6, I'd like to know more about your microcement.")}`}
    target="_blank"
    rel="noreferrer"
    className="wa-float"
  >
    <span className="wa-dot" />
    WhatsApp Us
  </a>
);

/* ---------- MAIN PAGE ---------- */
const HomePage = () => {
  const [finishes, setFinishes] = useState([]);
  const [textures, setTextures] = useState([]);
  const [applications, setApplications] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [f, a, d, j] = await Promise.all([
          axios.get(`${API}/finishes`),
          axios.get(`${API}/applications`),
          axios.get(`${API}/dealers`),
          axios.get(`${API}/journal`),
        ]);
        setFinishes(f.data.finishes || []);
        setTextures(f.data.textures || []);
        setApplications(a.data.applications || []);
        setDealers(d.data.dealers || []);
        setPosts(j.data.posts || []);
      } catch (e) {
        console.error("Failed to load site content", e);
      }
    })();
  }, []);

  return (
    <main data-testid="ga6-home">
      <Hero />
      <Marquee />
      <About />
      {finishes.length > 0 && <Finishes finishes={finishes} textures={textures} />}
      {applications.length > 0 && <Applications applications={applications} />}
      <Gallery />
      <Training />
      {dealers.length > 0 && <Dealers dealers={dealers} />}
      {posts.length > 0 && <Journal posts={posts} />}
      <FAQ />
      <Contact />
      <Footer />
      <WhatsAppFloat />
    </main>
  );
};

export default HomePage;
